import { Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { AuthService } from './auth.service';
import { GoalService } from './goal.service';
import { AccountDetailsService } from './account-details.service';

// Facade service that aggregates navbar-specific state without requiring
// direct dependencies on GoalService or AccountDetailsService.
// 
// This allows the navbar to function without forcing instantiation of
// heavy services when they're not needed (e.g., on the landing page).
@Injectable()
export class NavbarStateService {
  private hasGoals$ = new BehaviorSubject<boolean | undefined>(undefined);
  private isAdmin$ = new BehaviorSubject<boolean>(false);
  
  private goalUpdateSubscription?: Subscription;
  private isInitialised = false;

  // Public observables - navbar subscribes to these
  public readonly hasGoals: Observable<boolean | undefined> = this.hasGoals$.asObservable();
  public readonly isAdmin: Observable<boolean> = this.isAdmin$.asObservable();

  constructor(
    private authService: AuthService,
    private router: Router,
    @Optional() private goalService?: GoalService,
    @Optional() private accountDetailsService?: AccountDetailsService
  ) { }

  public initialiseIfNeeded(): void {
    if (this.isInitialised || !this.authService.isLoggedIn()) {
      return; // Already initialised or not logged in
    }

    // Check if we're on landing page - don't initialise if so
    const path = this.router.url;
    if (path === '/' || path === '/landing') {
      return; // On landing page, don't initialie
    }

    this.initialise();
  }

  // Public method to reset the service. Should be called when user logs out or navigates to landing page.
  public resetIfNeeded(): void {
    if (!this.isInitialised) {
      return; // Nothing to reset
    }

    const path = this.router.url;
    if (path === '/' || path === '/landing' || !this.authService.isLoggedIn()) {
      this.reset();
    }
  }

  //  Initialises state subscriptions to underlying services.
  //  Only called when logged in and not on landing page.
  private initialise(): void {
    if (this.isInitialised) {
      return; // Already initialized
    }

    this.isInitialised = true;

    // Subscribe to goal updates if GoalService is available
    // Note: We don't initialize GoalService here - it should initialize itself when needed (via guards/components)
    // This service is just a facade that aggregates state, not a service initializer
    if (this.goalService) {
      // Get initial hasGoals state (will be undefined if GoalService hasn't initialized yet)
      this.updateHasGoals();

      // Subscribe to goal update events
      this.goalUpdateSubscription = this.goalService.getGoalsUpdatedEvent().subscribe({
        next: () => this.updateHasGoals(),
        error: (error) => console.error(`*** NavbarStateService: Failed to subscribe to goals updated event. Error: ${error}`)
      });
    }

    // Get initial admin state if AccountDetailsService is available
    if (this.accountDetailsService) {
      this.updateIsAdmin();
    }
  }

  // Resets state and unsubscribes from services.
  // Called when user logs out or navigates to landing page.
  private reset(): void {
    if (!this.isInitialised) {
      return; // Nothing to reset
    }

    this.isInitialised = false;
    this.hasGoals$.next(undefined);
    this.isAdmin$.next(false);

    // Unsubscribe from goal updates
    if (this.goalUpdateSubscription) {
      this.goalUpdateSubscription.unsubscribe();
      this.goalUpdateSubscription = undefined;
    }
  }

  // Updates the hasGoals state by querying GoalService.
  private updateHasGoals(): void {
    if (!this.goalService) {
      this.hasGoals$.next(undefined);
      return;
    }

    this.goalService.getHasGoalsFast().subscribe({
      next: (hasGoals) => {
        this.hasGoals$.next(hasGoals);
      },
      error: () => {
        // Fall back to checking if service is initialized
        if (this.goalService?.isInitialised()) {
          const goals = this.goalService.getGoals();
          this.hasGoals$.next(goals.length > 0);
        } else {
          this.hasGoals$.next(undefined);
        }
      }
    });
  }

  // Updates the isAdmin state by querying AccountDetailsService.
  private updateIsAdmin(): void {
    if (!this.accountDetailsService) {
      this.isAdmin$.next(false);
      return;
    }

    this.accountDetailsService.getIsAdmin().subscribe({
      next: (isAdmin) => {
        this.isAdmin$.next(isAdmin);
      },
      error: (error) => {
        console.error(`*** NavbarStateService: Failed to get admin status. Error: ${error}`);
        this.isAdmin$.next(false);
      }
    });
  }

  // Manually refresh admin status (useful when admin status might have changed).
  public refreshAdminStatus(): void {
    if (this.isInitialised && this.accountDetailsService) {
      this.updateIsAdmin();
    }
  }
}
